import { WidgetBlockContainer } from '@metservice/mov-reports-package';
import { weatherWidgets } from '@metservice/platforms-weather-widgets';
import type { MovIntegrationMDSModel, WidgetblockModel } from '@metservicedev/platforms-services-interface/src';
import React, { useState } from 'react';

import { PlatmapContextProvider } from '@mono/platmap-context/src/platmap-context';
import { usePlatStageContext } from '@mono/use-plat-stage';

export interface IExternalWidgetPreviewProps {
    widgetblock: WidgetblockModel;
    getCfjsonsResponse: MovIntegrationMDSModel.GetCfjsonsResponse;
    datasets: MovIntegrationMDSModel.IDataset[];
    date: string;
    updateInterval?: number;
}

export const WidgetblockRenderer = ({ widgetblock, getCfjsonsResponse, datasets, date, updateInterval }: IExternalWidgetPreviewProps) => {
    const { type, state, timezone, theme } = widgetblock;
    const { stage } = usePlatStageContext();
    const [wwKey, setWwKey] = useState<string>();

    const weatherWidget = weatherWidgets.find((w) => w.type === type);
    if (weatherWidget) {
        return (
            <div
                style={{
                    height: '100%',
                    width: '100%',
                    boxShadow: '0px 2px 6px rgba(85, 91, 108, 0.3)',
                    borderRadius: 4,
                    padding: 12,
                    backgroundColor: theme === 'dark' ? '#23262F' : '#FFF',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                }}
            >
                <PlatmapContextProvider
                    controls={[
                        {
                            id: 'reset',
                            ReactIcon: RecenterIcon,
                            onClick: () => setWwKey(new Date().toISOString()),
                        },
                    ]}
                    viewportKey={wwKey}
                >
                    <weatherWidget.Render
                        settings={{ ...state, timezone, theme, isInteractive: false }}
                        stage={stage}
                        cfjsons={getCfjsonsResponse.data}
                        datasets={datasets}
                        theme={theme}
                        updateInterval={updateInterval} // 5 minutes
                    />
                </PlatmapContextProvider>
            </div>
        );
    }

    return (
        <div
            style={{
                height: '100%',
                boxShadow: '0px 2px 6px rgba(85, 91, 108, 0.3)',
                backgroundColor: theme === 'dark' ? '#23262F' : '#FFF',
                borderRadius: 4,
                padding: 4,
            }}
        >
            <WidgetBlockContainer
                widgetblockType={type}
                viewblockState={state}
                date={new Date(date)}
                timezone={timezone}
                stage={stage}
                renderingForPDF={false}
                updateInterval={1000 * 60 * 60}
                cfjsons={getCfjsonsResponse.data}
                datasets={datasets}
                isLoading={undefined}
                metjwt={undefined}
            />
        </div>
    );
};

// Found on figma, but not in metui icons
const RecenterIcon = () => {
    return (
        <svg width='16' height='17' viewBox='0 0 16 17' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <title>Recenter Icon</title>
            <path d='M2 5.67676V2.17676H5.5' stroke='white' strokeWidth='2' strokeMiterlimit='10' strokeLinecap='round' strokeLinejoin='round' />
            <path d='M14 10.6768L14 14.1768L10.5 14.1768' stroke='white' strokeWidth='2' strokeMiterlimit='10' strokeLinecap='round' strokeLinejoin='round' />
            <path d='M10.5 2.17676L14 2.17676L14 5.67676' stroke='white' strokeWidth='2' strokeMiterlimit='10' strokeLinecap='round' strokeLinejoin='round' />
            <path d='M5.5 14.1768L2 14.1768L2 10.6768' stroke='white' strokeWidth='2' strokeMiterlimit='10' strokeLinecap='round' strokeLinejoin='round' />
            <circle cx='8' cy='8.17676' r='2' fill='white' />
        </svg>
    );
};
